import API from "../../helpers/network/http";
import {Choir} from "../../utils/types";
import {normalizeChoirRehearsal} from "../../utils/choirRehearsal";


export default class ChoirService {
    // Create a method to create a song request
    static async createChoir(payload: Partial<Choir>) {
        // Call the register endpoint from the API
        const {data} = await API.post("/choirs", payload);

        // Return the response
        return data.data;
    }

    // Method to get choirs
    static async getChoirs(query: any): Promise<Choir[]> {
        // Call the login endpoint from the API
        const {data} = await API.get("/choirs", {params: query});

        // Return the response
        return data.data;
    }

    // Method to get a choir
    static async getChoir(id: any): Promise<Choir> {

        // Call the login endpoint from the API
        const {data} = await API.get(`/choirs/${id}`);

        // Return the response
        return data.data;
    }

    static async updateChoir(choirId: string, payload: Partial<Choir>) {
        const {data} = await API.put(`/choirs/${choirId}`, payload);
        return data.data;
    }

    // Method to get Choir Public Profile
    static async getChoirPublicProfile(uuid: string): Promise<Choir> {
        // Call the login endpoint from the API
        const {data} = await API.get(`/choirs/public/${uuid}`);

        // Return the response
        return data.data;
    }

    static async getDirectedChoirs(): Promise<Choir[]> {
        const {data} = await API.get('/choirs/director');
        return data.data;
    }

    static async followChoir(uuid: string) {
        const {data} = await API.post(`/choirs/${uuid}/follow`);
        return data.data;
    }

    static async unfollowChoir(uuid: string) {
        const {data} = await API.delete(`/choirs/${uuid}/follow`);
        return data.data;
    }

    static async getClaimablePublicChoir(uuid: string) {
        const {data} = await API.get(`/public-choirs/claim/${uuid}`);
        return data.data;
    }

    static async submitPublicChoirClaim(uuid: string, payload: {
        target_choir_uuid: string;
        proof_url?: string;
        proof_note?: string;
    }) {
        const {data} = await API.post(`/public-choirs/${uuid}/claim`, payload);
        return data.data;
    }

    // Method to get Choir Members
    static async getChoirMembers(query: any) {
        // Call the login endpoint from the API
        const {data} = await API.get(`/choir-members/choir/${query.uuid}`, {params: query.payload});

        // Return the response
        return data.data;
    }

    // Method to join a choir
    static async joinChoir(payload: string) {
        // Call the login endpoint from the API
        const {data} = await API.post(`/choir-members`, payload);

        // Return the response
        return data.data;
    }

    static async getAuditionStatus(choirId: string) {
        const {data} = await API.get(`/choir-members/audition-status/${choirId}`);
        return data.data;
    }

    // Method to get join requests
    static async getJoinRequests(query: any) {
        // Call the login endpoint from the API
        const {data} = await API.get(`/choir-members/join-requests/${query.choir_id}`);

        // Return the response
        return data.data;
    }

    // Method to get a join request
    static async getJoinRequest(id: string) {
        // Call the login endpoint from the API
        const {data} = await API.get(`/choir-members/user-join-requests/${id}`);

        // Return the response
        return data.data;
    }

    // Method to update a join request
    static async acceptJoinRequest(id: string, payload: any) {
        // Call the login endpoint from the API
        const {data} = await API.put(`/choir-members/join-requests/${id}`, payload);

        // Return the response
        return data.data;
    }

    // Method to reject a join request
    static async rejectJoinRequest(id: string, payload: any) {
        // Call the login endpoint from the API
        const {data} = await API.put(`/choir-members/join-requests/${id}`, payload);

        // Return the response
        return data.data;
    }

    // Method to make member a moderator
    static async makeMemberModerator(choir_id: string, user_email: string) {
        // Call the login endpoint from the API
        const {data} = await API.put(`/choir-members/${choir_id}`, {user_email, role: 'moderator'});

        // Return the response
        return data.data;
    }

    static async updateMemberGroup(choir_id: string, user_email: string, group: string) {
        const {data} = await API.put(`/choir-members/${choir_id}`, {user_email, group});
        return data.data;
    }

    // Method to create choir rehearsal
    static async createChoirRehearsal(payload: any) {
        // Call the login endpoint from the API
        const {data} = await API.post(`/choir-rehearsals`, payload);

        // Return the response
        return data.data;
    }

    // Method to get choir rehearsals
    static async getChoirRehearsals(query: any) {
        // Call the login endpoint from the API
        const {data} = await API.get(`/choir-rehearsals`, {params: query});

        // Return the response
        return data.data;
    }

    // Method to get choir rehearsals
    static async getChoirRehearsalSongs(rehearsal_id: any) {
        // Call the login endpoint from the API
        const {data} = await API.get(`/choir-rehearsals/song/${rehearsal_id}`);

        // Return the response
        return data.data;
    }

    // Method to get choir rehearsal documents
    static async getChoirRehearsalDocuments(rehearsal_id: any) {
        // Call the login endpoint from the API
        const {data} = await API.get(`/choir-rehearsals/documents/${rehearsal_id}`);

        // Return the response
        return data.data;
    }

    // Method to get a choir rehearsal
    static async getChoirRehearsal(choir_id: string, id: string) {
        // Call the login endpoint from the API
        const {data} = await API.get(`/choir-rehearsals/${id}?choir_id=${choir_id}`);

        // Return the response
        return normalizeChoirRehearsal(data.data);
    }

    // Method to delete a choir rehearsal
    static async deleteChoirRehearsal(rehearsal_id: string | number, choir_id: string) {
        const {data} = await API.delete(`/choir-rehearsals/${rehearsal_id}`, {
            params: { choir_id },
        });
        return data.data;
    }

    // Method to delete a choir rehearsal song
    static async deleteChoirRehearsalSong(
        rehearsal_id: string | number,
        song_uuid: string,
        choir_id: string,
        song_occurrence_uuid?: string,
    ) {
        const {data} = await API.delete(`/choir-rehearsals/song/${rehearsal_id}/${song_uuid}`, {
            params: { choir_id, song_occurrence_uuid },
        });
        return data.data;
    }

    static async updateChoirRehearsalSongLyrics(payload: {
        choir_id: string;
        rehearsal_id: string;
        song_id: string;
        lyrics?: any[];
        solfa_notation_1?: any[];
        solfa_notation_2?: any[];
        solfa_notation_3?: any[];
        solfa_notation_4?: any[];
        chord_progression?: any[];
        reference_audio?: {
            url: string;
            waveform?: any;
            duration?: number | null;
        } | null;
    }) {
        const {choir_id, ...body} = payload;
        const {data} = await API.put(`/choir-rehearsals/song-lyrics?choir_id=${choir_id}`, body);
        return data.data;
    }

    // Method to get user choir practise
    static async getUserChoirPractise(rehearsal_id: string, song_id: string) {
        // Call the login endpoint from the API
        const {data} = await API.get(`/choir-practise/user/${rehearsal_id}/${song_id}`);

        // Return the response
        return data.data;
    }

    // Method to get a member's choir practise
    static async getMemberChoirPractise(rehearsal_id: string, song_id: string, user_email: string, choir_id: string) {
        // Call the login endpoint from the API
        const {data} = await API.get(`/choir-practise/director/${rehearsal_id}/${song_id}/${user_email}?choir_id=${choir_id}`);

        // Return the response
        return data.data;
    }

    // Method to get user choir practise
    static async getMembersChoirPractise(rehearsal_id: string, song_id: string, choir_id: string) {
        // Call the login endpoint from the API
        const {data} = await API.get(`/choir-practise/${rehearsal_id}/${song_id}?choir_id=${choir_id}`);

        // Return the response
        return data.data;
    }

    static async getServiceTypes (choirId: number) {
            const res = await API.get(`/service-types/${choirId}`);
            return (res.data.data);
    };

    static async getExistingServiceDays (choirId: number) {
            const res = await API.get(`/choir-service-days/${choirId}`);
            const dayMap: Record<number, number> = {};
            res.data.data.forEach((entry: any) => {
                dayMap[entry.day_of_week] = entry.service_type_id;
            });
            return (dayMap);
    };

    static async saveServiceDays (choirId: number, payload: any) {
            const res = await API.post(`/choir-service-days/${choirId}`, payload);
            return (res.data.data);
    };

    static async getChoirSchedules (choirId: string) {
        // Call the login endpoint from the API
        const {data} = await API.get(`/choir-schedule/choir/${choirId}`);

        // Return the response
        return data.data;
    }

}
